import React from 'react'
import { MinusIcon } from '@heroicons/react/outline'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppToolTip } from '../AppToolTip/AppToolTip.component'
import { AppToolTipIcon } from '../AppToolTip/AppToolTipIcon'
import { AppUpTrendIcon } from '../AppSvgIcons/AppUpTrendIcon'
import { AppDownTrendIcon } from '../AppSvgIcons/AppDownTrendIcon'
import GradientCircleNotchIcon from '../SvgIcons/GradientCircleNotchIcon'

export type trendDirType = 'up' | 'down' | 'neutral'
interface StatsCardProps {
  title: string
  titleTooltip?: string
  subHeading: string
  subHeadingTooltip?: string
  value: string | number
  valueTooltip?: string
  changeValue: string
  changeTrend: trendDirType
  subValue?: {
    prefix: string
    value: string | number
    suffix: string
    trend: trendDirType
  }
  period: 'This Month' | 'YTD'
  onPeriodChange?: (period: 'This Month' | 'YTD') => void
  isLoading?: boolean
  errorMessage?: string
  errorMessageTooltip?: string
  hideMetric?: boolean
  dataSources?: string
}

export const AppStatsCard: React.FC<StatsCardProps> = ({
  title,
  titleTooltip,
  subHeading,
  subHeadingTooltip,
  value,
  valueTooltip,
  changeValue,
  changeTrend,
  subValue,
  period,
  onPeriodChange,
  isLoading,
  errorMessage,
  errorMessageTooltip,
  hideMetric,
  dataSources,
}) => {
  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return 'text-app-green-70'
      case 'down':
        return 'text-app-red-5'
      case 'neutral':
        return 'text-app-orange-15'
    }
  }

  const getTrendBgColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return 'bg-app-green-20'
      case 'down':
        return 'bg-app-red-20'
      case 'neutral':
        return 'bg-app-orange-20'
    }
  }

  const getTrendStrokeColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return '#629C28'
      case 'down':
        return '#D5394A'
      case 'neutral':
        return '#F07006'
    }
  }

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <AppUpTrendIcon stroke={getTrendStrokeColor(trend)} />
      case 'down':
        return <AppDownTrendIcon stroke={getTrendStrokeColor(trend)} />
      case 'neutral':
        return <MinusIcon className="w-4 h-4" stroke={getTrendStrokeColor(trend)} />
    }
  }

  return (
    <div className="w-full">
      <div className="space-y-4 smd:space-y-0 smd:flex smd:items-center smd:justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-normal font-inter">{title}</h2>
          {titleTooltip && (
            <AppToolTip variant="primary" name={`stats-card-${title}-title`} text={titleTooltip}>
              <AppToolTipIcon />
            </AppToolTip>
          )}
        </div>
        <div className="flex items-center gap-2">
          <AppButton
            onClick={() => onPeriodChange?.('This Month')}
            variant={period === 'This Month' ? 'primary' : 'secondary'}
            size="sm"
            label="This Month"
          />
          <AppButton
            onClick={() => onPeriodChange?.('YTD')}
            variant={period === 'YTD' ? 'primary' : 'secondary'}
            size="sm"
            label="YTD"
          />
          <AppToolTip variant="primary" name={`stats-card-${title}-ytd`} text={'Year to date'}>
            <AppToolTipIcon />
          </AppToolTip>
        </div>
      </div>

      {!isLoading ? (
        <>
          {!hideMetric ? (
            <>
              {errorMessage ? (
                <div className="h-[120px] bg-app-white-100 flex items-center justify-center font-medium text-sm rounded-xl border border-app-grey-25 space-x-2 shadow-md">
                  <span className="text-app-grey-90 text-sm font-inter font-normal">
                    {errorMessage}
                  </span>
                  {errorMessageTooltip && (
                    <AppToolTip
                      variant="primary"
                      name={`stats-card-${title}-client-count`}
                      text={errorMessageTooltip}
                    >
                      <AppToolTipIcon />
                    </AppToolTip>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-app-grey-30 px-6 py-4 shadow-md min-h-[120px]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-app-grey-90 text-sm font-inter font-normal">
                        {subHeading}
                      </span>
                      {subHeadingTooltip && (
                        <AppToolTip
                          variant="primary"
                          name={`stats-card-${title}-client-count`}
                          text={subHeadingTooltip}
                        >
                          <AppToolTipIcon />
                        </AppToolTip>
                      )}
                    </div>
                    {dataSources && (
                      <div className="text-app-grey-90 text-sm font-inter font-normal">
                        {dataSources}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-2xl font-normal font-inter">{value}</h3>
                      {valueTooltip && (
                        <AppToolTip
                          variant="primary"
                          name={`stats-card-${title}-value`}
                          text={valueTooltip}
                        >
                          <AppToolTipIcon />
                        </AppToolTip>
                      )}
                    </div>
                    {changeValue && (
                      <div
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md ${getTrendBgColor(
                          changeTrend
                        )}`}
                      >
                        {getTrendIcon(changeTrend)}
                        <span
                          className={`${getTrendColor(
                            changeTrend
                          )} ml-1 text-sm font-inter font-normal`}
                        >
                          {changeValue}
                        </span>
                      </div>
                    )}
                  </div>

                  {subValue && (
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-1`}>
                        <span
                          className={`${getTrendColor(
                            subValue.trend
                          )} text-sm font-inter font-normal`}
                        >
                          {subValue.prefix}
                        </span>
                        <span
                          className={`${getTrendColor(
                            subValue.trend
                          )} text-sm font-inter font-normal`}
                        >
                          {subValue.value}
                        </span>
                      </div>
                      <span
                        className={`${getTrendColor(
                          subValue.trend
                        )} text-sm font-inter font-normal`}
                      >
                        {subValue.suffix}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="h-[120px] bg-app-white-100 flex items-center justify-center font-medium text-sm rounded-xl border border-app-grey-25">
              You cannot view this information
            </div>
          )}
        </>
      ) : (
        <div className="flex justify-center items-center px-6 py-4 rounded-lg border border-app-grey-30">
          <GradientCircleNotchIcon width="100" height="100" />
        </div>
      )}
    </div>
  )
}
